// Configure fields inside one signature_party sandbox block.

import {
  INTENT_AUTOFILL_VARIABLE_KEYS,
  INTENT_AUTOFILL_VARIABLE_LABELS,
  INTENT_FILL_KINDS,
  createEmptyIntentFillField,
  type IntentFillField,
  type IntentFillKind,
} from "@/lib/opportunity/proposalIntentLayout";
import type { ProposalSandboxSignaturePartyBlock } from "@/lib/opportunity/proposalSandboxBlocks";

type Props = {
  block: ProposalSandboxSignaturePartyBlock;
  onChange: (block: ProposalSandboxSignaturePartyBlock) => void;
};

export function ProposalSandboxPartyFieldsEditor({ block, onChange }: Props) {
  function updateField(fieldId: string, patch: Partial<IntentFillField>) {
    onChange({
      ...block,
      fields: block.fields.map((f) => (f.id === fieldId ? { ...f, ...patch } : f)),
    });
  }

  function removeField(fieldId: string) {
    onChange({
      ...block,
      fields: block.fields.filter((f) => f.id !== fieldId),
    });
  }

  function addField(kind: IntentFillKind) {
    onChange({
      ...block,
      fields: [...block.fields, createEmptyIntentFillField(kind)],
    });
  }

  return (
    <div className="space-y-2">
      <input
        type="text"
        value={block.title}
        onChange={(e) => onChange({ ...block, title: e.target.value })}
        className="w-full rounded border border-gray-200 px-2 py-1 text-xs dark:border-zinc-700 dark:bg-zinc-800"
        aria-label="Party title"
        placeholder="Party title"
      />
      {block.fields.map((field) => (
        <div
          key={field.id}
          className="grid gap-1.5 sm:grid-cols-[7rem_1fr_8rem_auto] sm:items-center"
        >
          <select
            value={field.kind}
            onChange={(e) =>
              updateField(field.id, { kind: e.target.value as IntentFillKind })
            }
            className="rounded border border-gray-200 px-1.5 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
            aria-label="Field kind"
          >
            {INTENT_FILL_KINDS.map((k) => (
              <option key={k} value={k}>
                {k}
              </option>
            ))}
          </select>
          <input
            type="text"
            value={field.label}
            onChange={(e) => updateField(field.id, { label: e.target.value })}
            className="rounded border border-gray-200 px-2 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
            aria-label="Field label"
          />
          <select
            value={field.variable_key ?? ""}
            onChange={(e) =>
              updateField(field.id, { variable_key: e.target.value || null })
            }
            className="rounded border border-gray-200 px-1.5 py-1 text-[11px] dark:border-zinc-700 dark:bg-zinc-800"
            aria-label="Autofill variable"
          >
            <option value="">No autofill</option>
            {INTENT_AUTOFILL_VARIABLE_KEYS.map((k) => (
              <option key={k} value={k}>
                {INTENT_AUTOFILL_VARIABLE_LABELS[k]}
              </option>
            ))}
          </select>
          <button
            type="button"
            onClick={() => removeField(field.id)}
            disabled={block.fields.length <= 1}
            className="rounded border border-gray-300 px-1.5 py-0.5 text-[11px] disabled:opacity-40 dark:border-zinc-600"
          >
            ×
          </button>
        </div>
      ))}
      <div className="flex flex-wrap gap-1">
        {INTENT_FILL_KINDS.map((kind) => (
          <button
            key={kind}
            type="button"
            onClick={() => addField(kind)}
            className="rounded border border-dashed border-gray-300 px-2 py-0.5 text-[10px] text-gray-600 dark:border-zinc-600 dark:text-zinc-300"
          >
            + {kind}
          </button>
        ))}
      </div>
    </div>
  );
}
